/**
 * Corrections to the planning engine that change a saved plan's figures. Each plan records the engine
 * revision it was last frozen or created under (`tax_plans.engine_revision`); an open plan from an
 * earlier revision shows what changed and why. Approved and shared plans keep their saved snapshot,
 * so the client's copy never moves.
 *
 * Revision 1 (28 October 2026, WP-00): the 2026 §68 limit after the QBI deduction, tips and overtime
 * reduced in full $1,000 steps, Virginia's standard deduction reverting after 2029.
 */
import type { StateCode } from "./state";
import type { YearResult } from "./strategies";
import { tableFor, type FilingStatus } from "./tables";

/** The engine revision new plans are computed under. Raise it with each correction listed below. */
export const PLANNING_ENGINE_REVISION = 1;
export const PLANNING_CORRECTED_ON = "2026-10-28";

export type PlanCorrectionKey = "itemized_limit_after_qbi" | "tips_overtime_full_steps" | "virginia_deduction_after_2029";
export type PlanCorrection = { key: PlanCorrectionKey; years: number[]; text: string };

/** "2030", "2027 and 2028", "2027 to 2031" (a run of three or more), or "2027, 2029 and 2031". */
const yearsText = (years: number[]) => {
  const last = years[years.length - 1]!;
  if (years.length === 1) return String(last);
  if (years.length >= 3 && last - years[0]! === years.length - 1) return `${years[0]} to ${last}`;
  return `${years.slice(0, -1).join(", ")} and ${last}`;
};

/**
 * The corrections that can move this plan's figures, with the years each touches, from its projected
 * scenarios. Empty for a plan computed under the current revision.
 */
export function planCorrections(plan: { engineRevision: number; filingStatus: FilingStatus; stateCode: StateCode; inflationPct: number }, scenarios: YearResult[][]): PlanCorrection[] {
  if (plan.engineRevision >= PLANNING_ENGINE_REVISION) return [];
  const limit = new Set<number>();
  const steps = new Set<number>();
  const virginia = new Set<number>();
  const st = plan.filingStatus;
  for (const results of scenarios)
    for (const r of results) {
      const t = tableFor(r.year, plan.inflationPct);
      const f = r.federal;
      const below = f.seniorDeduction + f.tipsDeduction + f.overtimeDeduction;
      // The limit was measured before QBI: a plan with QBI, itemized deductions and income in the 37% bracket.
      if (t.itemizedLimit37 && f.qbiDeduction > 0 && f.itemizedDeductions > 0 && f.agi - below > t.brackets[st][t.brackets[st].length - 1]!) limit.add(r.year);
      // The reduction was 10% of every dollar over the threshold: any tips or overtime deduction above it.
      if ((f.tipsDeduction > 0 && t.tips && f.agi > t.tips.phaseStart[st]) || (f.overtimeDeduction > 0 && t.overtime && f.agi > t.overtime.phaseStart[st])) steps.add(r.year);
      if (plan.stateCode === "VA" && r.state.note) virginia.add(r.year);
    }
  const out: PlanCorrection[] = [];
  const sorted = (s: Set<number>) => [...s].sort((a, b) => a - b);
  if (limit.size) {
    const y = sorted(limit);
    out.push({ key: "itemized_limit_after_qbi", years: y, text: `${yearsText(y)}: the limit on itemized deductions in the 37% bracket is now measured after the QBI deduction, as IRC §68 and §199A require. The QBI deduction, itemized deductions and tax can differ.` });
  }
  if (steps.size) {
    const y = sorted(steps);
    out.push({ key: "tips_overtime_full_steps", years: y, text: `${yearsText(y)}: the tips and overtime deductions are now reduced by $100 for each full $1,000 of income over the threshold, as Schedule 1-A works them. Each deduction can be up to $99 higher.` });
  }
  if (virginia.size) {
    const y = sorted(virginia);
    out.push({ key: "virginia_deduction_after_2029", years: y, text: `${yearsText(y)}: Virginia's standard deduction returns to $3,000 ($6,000 joint) under current law, so Virginia tax is higher in these years.` });
  }
  return out;
}
